import {useQueryClient} from '@tanstack/react-query';
import React, {useCallback, useMemo, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {
  CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS,
  CONTENT_SUBMISSION_ANALYTICS_BUTTONS as ANALYTICS_BUTTONS,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import type {ContentSubmission} from '@/types/contentSubmission';
import ContentSubmissionSubHeader from '@/views/campaigns/components/content-submissions/content-submission-sub-header';
import ContentSubmissionsGrid from '@/views/campaigns/components/content-submissions/content-submissions-grid';
import {
  PreviousButton,
  NextButton,
} from '@/components/content-navigation/ContentNavigation';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event/useTrackButtonEvent';
import useContentSubmissionQuery, {
  CONTENT_SUBMISSION_QUERY_KEY,
} from '@/hooks/queries/use-content-submission-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useContentSubmissionLink from '@/hooks/use-content-submission-link';
import useContentSubmissionNavigation from '@/hooks/use-content-submission-navigation';
import styles from './MixedMediaView.module.scss';

type MixedMediaViewProps = {
  isOffPlatform?: boolean;
};

function MixedMediaView({isOffPlatform = false}: MixedMediaViewProps) {
  const {campaignId, parentContentSubmissionId} = useParams<{
    campaignId: string;
    parentContentSubmissionId: string;
  }>();

  const {data: parentContentSubmission} = useContentSubmissionQuery({
    contentSubmissionId: parentContentSubmissionId,
    enabled: !!parentContentSubmissionId,
  });

  const {dashboardUser} = useDashboardUserQuery({});
  const isAdmin = dashboardUser?.isAdmin;

  const {pressed: pressedButton} = useTrackButtonEvent();

  const queryClient = useQueryClient();
  const getLink = useContentSubmissionLink();
  const scrollRef = useRef<HTMLDivElement>(null);
  const {previousLink, nextLink} = useContentSubmissionNavigation();

  const parentProfile = parentContentSubmission?.profile;
  const childSubmissions = useMemo(
    () =>
      (parentContentSubmission?.childContentSubmissions ?? []).map((child) =>
        child.profile ? child : {...child, profile: parentProfile}
      ),
    [parentContentSubmission?.childContentSubmissions, parentProfile]
  );

  const campaignNetwork = parentContentSubmission?.campaign?.network ?? null;
  const campaignState =
    parentContentSubmission?.campaign?.aasmState ??
    parentContentSubmission?.campaign?.state ??
    null;

  const getChildLink = useCallback(
    (child: ContentSubmission) =>
      getLink({
        parentContentSubmissionId,
        contentSubmissionId: child.id,
      }),
    [getLink, parentContentSubmissionId]
  );

  const handleTransitionSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [CONTENT_SUBMISSION_QUERY_KEY, parentContentSubmissionId],
    });
  }, [parentContentSubmissionId]);

  if (!parentContentSubmission) {
    return null;
  }

  const navContext = {
    screen_name: ANALYTICS_SCREENS.ASSET_MIXED_MEDIA,
    campaign_id: campaignId,
    creator_id: parentContentSubmission.profile?.id,
    flow_id: parentContentSubmissionId,
  };

  return (
    <div className={styles.page}>
      <ContentSubmissionSubHeader
        contentSubmission={parentContentSubmission}
        activeVersionId={null}
        onVersionChange={() => {}}
        contentTypeLabelOverride={translate('Story')}
      />
      <div className={styles.mainContent}>
        <div className={styles.navButton}>
          {previousLink ? (
            <PreviousButton
              to={previousLink}
              onClick={() =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.NAV_PREVIOUS,
                  ...navContext,
                })
              }
            />
          ) : null}
        </div>
        <div ref={scrollRef} className={styles.gridContainer}>
          <ContentSubmissionsGrid
            activeFiltersKey={parentContentSubmissionId}
            contentSubmissions={childSubmissions}
            campaignNetwork={campaignNetwork}
            campaignState={campaignState}
            getCardLink={getChildLink}
            scrollRef={scrollRef}
            isAdmin={isAdmin}
            isOffPlatform={isOffPlatform}
            onTransitionSuccess={handleTransitionSuccess}
            screenName={ANALYTICS_SCREENS.ASSET_MIXED_MEDIA}
          />
        </div>
        <div className={styles.navButton}>
          {nextLink ? (
            <NextButton
              to={nextLink}
              onClick={() =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.NAV_NEXT,
                  ...navContext,
                })
              }
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default MixedMediaView;
